/**
 * CampaignsManagerTab — iter181
 *
 * Full CRUD surface for the dynamic Campaign Manager. Replaces the two
 * hard-coded launch offers (Beta Individual Plus & Yanbu Dealers) with
 * a single, admin-managed table so ANY current or future promotion is
 * a database record instead of code.
 *
 * Backend contract:
 *   GET    /api/admin/campaigns[?q=&status=]
 *   POST   /api/admin/campaigns
 *   PATCH  /api/admin/campaigns/{id}
 *   DELETE /api/admin/campaigns/{id}
 *   POST   /api/admin/campaigns/{id}/toggle
 *   POST   /api/admin/campaigns/{id}/archive
 *   POST   /api/admin/campaigns/{id}/unarchive
 *   POST   /api/admin/campaigns/{id}/duplicate
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Megaphone, Plus, Search, RefreshCw, Loader2, Trash2, Edit3, X, Check,
  Play, Pause, Copy, Archive, ArchiveRestore, Calendar as CalIcon,
  Percent, Users as UsersIcon, MapPin, Tag as TagIcon, Info, Filter,
  BarChart3, MoreVertical,
} from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { apiClient, formatApiError } from "../../lib/api";

const OFFER_TYPES = [
  { id: "free_subscription", ar: "اشتراك مجاني",       en: "Free subscription" },
  { id: "percent_discount",  ar: "خصم بنسبة مئوية",     en: "Percent discount" },
  { id: "fixed_discount",    ar: "خصم بقيمة ثابتة",     en: "Fixed discount" },
  { id: "plan_upgrade",      ar: "ترقية باقة",          en: "Plan upgrade" },
  { id: "extra_benefits",    ar: "مزايا إضافية",        en: "Extra benefits" },
];

const PLANS = [
  { id: "",                ar: "— بدون —",          en: "— None —" },
  { id: "individual_plus", ar: "الأفراد بلس",       en: "Individual Plus" },
  { id: "dealer_pro",      ar: "التاجر برو",        en: "Dealer Pro" },
  { id: "enterprise",      ar: "المؤسسات",          en: "Enterprise" },
];

const CATEGORY_OPTIONS = [
  { id: "individual", ar: "أفراد",       en: "Individuals" },
  { id: "dealer",     ar: "معارض",       en: "Dealers" },
  { id: "business",   ar: "شركات",       en: "Businesses" },
  { id: "workshop",   ar: "ورش",         en: "Workshops" },
  { id: "rental",     ar: "مكاتب تأجير", en: "Rentals" },
  { id: "carrier",    ar: "ناقلين",      en: "Carriers" },
];

const offerTypeLabel = (id, lang) =>
  (OFFER_TYPES.find((x) => x.id === id) || {})[lang === "ar" ? "ar" : "en"] || id;
const planLabel = (id, lang) =>
  (PLANS.find((x) => x.id === id) || {})[lang === "ar" ? "ar" : "en"] || (id || "—");
const catLabel = (id, lang) =>
  (CATEGORY_OPTIONS.find((x) => x.id === id) || {})[lang === "ar" ? "ar" : "en"] || id;

function statusOf(c) {
  const now = Date.now();
  if (c.archived_at) return "archived";
  if (!c.is_active) return "paused";
  if (c.end_at && new Date(c.end_at).getTime() < now) return "expired";
  if (c.start_at && new Date(c.start_at).getTime() > now) return "scheduled";
  return "active";
}
function statusColor(s) {
  return ({
    active:    "#0f7c3a",
    paused:    "#c68a12",
    archived:  "#525252",
    expired:   "#b91c1c",
    scheduled: "#0b3d91",
  })[s] || "#525252";
}
function statusLabel(s, lang) {
  return ({
    active:    lang === "ar" ? "مفعّلة"   : "Active",
    paused:    lang === "ar" ? "متوقفة"  : "Paused",
    archived:  lang === "ar" ? "مؤرشفة"  : "Archived",
    expired:   lang === "ar" ? "منتهية"  : "Expired",
    scheduled: lang === "ar" ? "مجدولة"  : "Scheduled",
  })[s] || s;
}

export default function CampaignsManagerTab() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("created_desc");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // "new" | campaign object | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q: q || undefined };
      if (filter !== "all") params.status = filter;
      const { data } = await apiClient.get("/admin/campaigns", { params });
      setItems(data.items || []);
      setStats(data.stats || null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [q, filter]);
  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    const arr = items.slice();
    switch (sort) {
      case "created_asc":  arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")); break;
      case "usage_desc":   arr.sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0)); break;
      case "name_asc":     arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")); break;
      case "end_asc":      arr.sort((a, b) => (a.end_at || "z").localeCompare(b.end_at || "z")); break;
      default:             arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return arr;
  }, [items, sort]);

  const toggle = async (c) => {
    try {
      await apiClient.post(`/admin/campaigns/${c.id}/toggle`);
      toast.success(lang === "ar" ? "تم تحديث الحالة" : "Status updated");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const archive = async (c) => {
    try {
      await apiClient.post(`/admin/campaigns/${c.id}/archive`);
      toast.success(lang === "ar" ? "تمت الأرشفة" : "Archived");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const unarchive = async (c) => {
    try {
      await apiClient.post(`/admin/campaigns/${c.id}/unarchive`);
      toast.success(lang === "ar" ? "تم استعادة الحملة" : "Restored");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const duplicate = async (c) => {
    try {
      await apiClient.post(`/admin/campaigns/${c.id}/duplicate`);
      toast.success(lang === "ar" ? "تم النسخ" : "Duplicated");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const remove = async (c) => {
    if (!window.confirm(lang === "ar" ? "حذف الحملة نهائياً؟" : "Delete permanently?")) return;
    try {
      await apiClient.delete(`/admin/campaigns/${c.id}`);
      toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  return (
    <div data-testid="campaigns-manager-tab" className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-[#a8893e]" />
          <h2 className="font-display text-2xl">
            {lang === "ar" ? "إدارة الحملات والعروض" : "Campaigns & Offers"}
          </h2>
        </div>
        <button onClick={() => setEditing("new")} className="btn-primary text-sm" data-testid="new-campaign">
          <Plus className="w-4 h-4" /> {lang === "ar" ? "حملة جديدة" : "New campaign"}
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="campaigns-stats">
          <StatCard color="#0f7c3a" label={lang === "ar" ? "نشطة" : "Active"} value={stats.active} icon={Play} />
          <StatCard color="#c68a12" label={lang === "ar" ? "متوقفة" : "Paused"} value={stats.paused} icon={Pause} />
          <StatCard color="#b91c1c" label={lang === "ar" ? "منتهية" : "Expired"} value={stats.expired} icon={CalIcon} />
          <StatCard color="#a8893e" label={lang === "ar" ? "المستفيدون" : "Beneficiaries"} value={stats.beneficiaries_total} icon={UsersIcon} />
        </div>
      )}

      {/* Search + filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute start-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a8a]" />
          <input
            className="input-dark ps-8 w-full"
            placeholder={lang === "ar" ? "بحث بالاسم أو الوصف أو الوسم…" : "Search name / description / tag…"}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            data-testid="campaigns-search"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input-dark text-[13px]"
          data-testid="campaigns-filter"
        >
          <option value="all">{lang === "ar" ? "كل الحالات" : "All"}</option>
          <option value="active">{lang === "ar" ? "نشطة" : "Active"}</option>
          <option value="paused">{lang === "ar" ? "متوقفة" : "Paused"}</option>
          <option value="expired">{lang === "ar" ? "منتهية" : "Expired"}</option>
          <option value="archived">{lang === "ar" ? "مؤرشفة" : "Archived"}</option>
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="input-dark text-[13px]"
          data-testid="campaigns-sort"
        >
          <option value="created_desc">{lang === "ar" ? "الأحدث" : "Newest"}</option>
          <option value="created_asc">{lang === "ar" ? "الأقدم" : "Oldest"}</option>
          <option value="usage_desc">{lang === "ar" ? "الأكثر استخداماً" : "Most redeemed"}</option>
          <option value="name_asc">{lang === "ar" ? "الاسم" : "Name"}</option>
          <option value="end_asc">{lang === "ar" ? "أقرب انتهاء" : "Ending soonest"}</option>
        </select>
        <button onClick={load} className="btn-ghost text-sm" data-testid="campaigns-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> {lang === "ar" ? "تحديث" : "Refresh"}
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#a8893e]" /></div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 text-[#8a8a8a]" data-testid="campaigns-empty">
          {lang === "ar" ? "لا توجد حملات مطابقة." : "No matching campaigns."}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[#0a0a0a]/[0.08]">
          <table className="w-full text-sm">
            <thead className="bg-[#fafaf7] text-[#525252]">
              <tr>
                <th className="text-start p-3">{lang === "ar" ? "الحملة" : "Campaign"}</th>
                <th className="text-start p-3">{lang === "ar" ? "النوع" : "Type"}</th>
                <th className="text-start p-3">{lang === "ar" ? "الاستهداف" : "Targeting"}</th>
                <th className="text-start p-3">{lang === "ar" ? "المدة" : "Window"}</th>
                <th className="text-start p-3">{lang === "ar" ? "الاستفادة" : "Redemptions"}</th>
                <th className="text-start p-3">{lang === "ar" ? "الحالة" : "Status"}</th>
                <th className="text-end p-3">{lang === "ar" ? "إجراءات" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => {
                const st = statusOf(c);
                return (
                  <tr key={c.id} className="border-t border-[#0a0a0a]/[0.06]" data-testid={`campaign-row-${c.id}`}>
                    <td className="p-3">
                      <div className="font-medium text-[#0a0a0a]">{c.name}</div>
                      <div className="text-[11px] text-[#8a8a8a] line-clamp-1">{c.description}</div>
                      {c.linked_flag_key && (
                        <div className="mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded bg-[#fbf7ec] text-[#8a6f2e]">
                          {lang === "ar" ? "حملة إطلاق (تراثية)" : "legacy launch"}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="text-[12px]">{offerTypeLabel(c.offer_type, lang)}</div>
                      <div className="text-[11px] text-[#8a8a8a]">
                        {c.granted_plan ? planLabel(c.granted_plan, lang) : ""}
                        {c.discount_percent ? ` · ${c.discount_percent}%` : ""}
                        {c.free_duration_days ? ` · ${c.free_duration_days}d` : ""}
                      </div>
                    </td>
                    <td className="p-3 text-[12px] text-[#525252]">
                      <div className="flex flex-wrap gap-1">
                        {(c.target_categories?.length ? c.target_categories : ["all"]).slice(0, 3).map((t) => (
                          <span key={t} className="px-1.5 py-0.5 rounded-full bg-[#0a0a0a]/[0.05]">
                            {t === "all" ? (lang === "ar" ? "الكل" : "All") : catLabel(t, lang)}
                          </span>
                        ))}
                      </div>
                      {c.target_cities?.length ? (
                        <div className="mt-1 text-[11px] text-[#8a8a8a] flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {c.target_cities.join(", ")}
                        </div>
                      ) : null}
                    </td>
                    <td className="p-3 text-[11px] text-[#525252]">
                      {c.start_at ? c.start_at.slice(0, 10) : "—"}
                      <br />
                      {c.end_at ? c.end_at.slice(0, 10) : "∞"}
                    </td>
                    <td className="p-3 text-[12px]">
                      <span className="font-mono">{c.usage_count || 0}</span>
                      {c.max_beneficiaries ? <span className="text-[#8a8a8a]"> / {c.max_beneficiaries}</span> : null}
                    </td>
                    <td className="p-3">
                      <span
                        className="inline-block px-2 py-1 rounded-full text-[11px] font-medium"
                        style={{ background: `${statusColor(st)}18`, color: statusColor(st) }}
                      >{statusLabel(st, lang)}</span>
                    </td>
                    <td className="p-3">
                      {/* Desktop — inline icons (unchanged) */}
                      <div className="hidden sm:flex items-center justify-end gap-1">
                        <IconBtn title={c.is_active ? (lang==="ar"?"إيقاف":"Pause") : (lang==="ar"?"تفعيل":"Activate")} onClick={() => toggle(c)} testid={`toggle-${c.id}`}>
                          {c.is_active ? <Pause className="w-4 h-4 text-[#c68a12]" /> : <Play className="w-4 h-4 text-[#0f7c3a]" />}
                        </IconBtn>
                        <IconBtn title={lang === "ar" ? "تعديل" : "Edit"} onClick={() => setEditing(c)} testid={`edit-${c.id}`}>
                          <Edit3 className="w-4 h-4 text-[#525252]" />
                        </IconBtn>
                        <IconBtn title={lang === "ar" ? "نسخ" : "Duplicate"} onClick={() => duplicate(c)} testid={`dup-${c.id}`}>
                          <Copy className="w-4 h-4 text-[#525252]" />
                        </IconBtn>
                        {c.archived_at ? (
                          <IconBtn title={lang === "ar" ? "استعادة" : "Restore"} onClick={() => unarchive(c)} testid={`unarch-${c.id}`}>
                            <ArchiveRestore className="w-4 h-4 text-[#0b3d91]" />
                          </IconBtn>
                        ) : (
                          <IconBtn title={lang === "ar" ? "أرشفة" : "Archive"} onClick={() => archive(c)} testid={`arch-${c.id}`}>
                            <Archive className="w-4 h-4 text-[#525252]" />
                          </IconBtn>
                        )}
                        {!c.linked_flag_key && (
                          <IconBtn title={lang === "ar" ? "حذف" : "Delete"} onClick={() => remove(c)} testid={`del-${c.id}`}>
                            <Trash2 className="w-4 h-4 text-[#b91c1c]" />
                          </IconBtn>
                        )}
                      </div>
                      {/* Mobile — single ⋮ menu holding the same actions */}
                      <div className="sm:hidden flex justify-end">
                        <CampaignActionsMenu
                          c={c} lang={lang}
                          onToggle={() => toggle(c)}
                          onEdit={() => setEditing(c)}
                          onDuplicate={() => duplicate(c)}
                          onArchive={() => archive(c)}
                          onUnarchive={() => unarchive(c)}
                          onDelete={() => remove(c)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CampaignForm
          lang={lang}
          seed={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ color, label, value, icon: Icon }) {
  return (
    <div
      className="rounded-xl p-3.5 border"
      style={{ borderColor: `${color}30`, background: `${color}08` }}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color }} />
        <div className="text-[11px] text-[#525252]">{label}</div>
      </div>
      <div className="text-2xl font-display" style={{ color }}>{value ?? 0}</div>
    </div>
  );
}

function IconBtn({ onClick, title, children, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="p-1.5 rounded hover:bg-[#0a0a0a]/5"
      data-testid={testid}
    >
      {children}
    </button>
  );
}

// iter187 — mobile-only ⋮ menu that surfaces the SAME actions as the
// desktop icon row. Rendered exclusively inside `sm:hidden` wrappers,
// so the desktop UI stays untouched. All handlers are pass-through —
// no logic, no permission changes, no field renames.
function CampaignActionsMenu({ c, lang, onToggle, onEdit, onDuplicate, onArchive, onUnarchive, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const call = (fn) => () => { setOpen(false); fn?.(); };

  const items = [
    {
      key: "toggle",
      icon: c.is_active ? Pause : Play,
      color: c.is_active ? "#c68a12" : "#0f7c3a",
      label: c.is_active ? (lang === "ar" ? "إيقاف" : "Pause") : (lang === "ar" ? "تفعيل" : "Activate"),
      onClick: call(onToggle),
      testid: `m-toggle-${c.id}`,
    },
    { key: "edit", icon: Edit3, color: "#525252", label: lang === "ar" ? "تعديل" : "Edit", onClick: call(onEdit), testid: `m-edit-${c.id}` },
    { key: "dup",  icon: Copy,  color: "#525252", label: lang === "ar" ? "نسخ الحملة" : "Duplicate", onClick: call(onDuplicate), testid: `m-dup-${c.id}` },
    c.archived_at
      ? { key: "unarch", icon: ArchiveRestore, color: "#0b3d91", label: lang === "ar" ? "استعادة" : "Restore",  onClick: call(onUnarchive), testid: `m-unarch-${c.id}` }
      : { key: "arch",   icon: Archive,        color: "#525252", label: lang === "ar" ? "أرشفة"   : "Archive",  onClick: call(onArchive),   testid: `m-arch-${c.id}` },
  ];
  if (!c.linked_flag_key) {
    items.push({ key: "del", icon: Trash2, color: "#b91c1c", label: lang === "ar" ? "حذف" : "Delete", onClick: call(onDelete), testid: `m-del-${c.id}`, danger: true });
  }

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-lg hover:bg-[#0a0a0a]/[0.05]"
        aria-label={lang === "ar" ? "خيارات" : "Actions"}
        data-testid={`m-menu-${c.id}`}
      >
        <MoreVertical className="w-4 h-4 text-[#3a3a3a]" />
      </button>
      {open && (
        <div
          ref={ref}
          className="absolute end-0 mt-1 w-56 bg-white rounded-xl border border-[#0a0a0a]/[0.10] shadow-lg z-30 overflow-hidden"
          data-testid={`m-menu-panel-${c.id}`}
        >
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <button
                key={it.key}
                onClick={it.onClick}
                className={`w-full text-start px-3 py-2.5 flex items-center gap-2 text-[13px] transition-colors ${it.danger ? "text-red-700 hover:bg-red-50" : "text-[#3a3a3a] hover:bg-[#fafaf7]"}`}
                data-testid={it.testid}
              >
                <Icon className="w-4 h-4 shrink-0" style={{ color: it.color }} />
                <span className="flex-1">{it.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Create/Edit form modal ────────────────────────────────────────
function CampaignForm({ lang, seed, onClose, onSaved }) {
  const isEdit = !!seed;
  const [f, setF] = useState(() => ({
    name: seed?.name || "",
    description: seed?.description || "",
    is_active: seed?.is_active ?? true,
    visible: seed?.visible ?? true,
    start_at: seed?.start_at ? seed.start_at.slice(0, 10) : "",
    end_at: seed?.end_at ? seed.end_at.slice(0, 10) : "",
    offer_type: seed?.offer_type || "free_subscription",
    free_duration_days: seed?.free_duration_days ?? 0,
    discount_percent: seed?.discount_percent ?? 0,
    discount_amount: seed?.discount_amount ?? 0,
    granted_plan: seed?.granted_plan || "",
    extra_benefits: (seed?.extra_benefits || []).join(", "),
    target_categories: seed?.target_categories || [],
    target_cities: (seed?.target_cities || []).join(", "),
    requires_verification: !!seed?.requires_verification,
    requires_kyc: !!seed?.requires_kyc,
    required_activity: (seed?.required_activity || []).join(", "),
    requires_prior_subscription: !!seed?.requires_prior_subscription,
    requires_admin_approval: !!seed?.requires_admin_approval,
    max_beneficiaries: seed?.max_beneficiaries ?? 0,
    tags: (seed?.tags || []).join(", "),
  }));
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleCategory = (id) => setF((s) => ({
    ...s,
    target_categories: s.target_categories.includes(id)
      ? s.target_categories.filter((x) => x !== id)
      : [...s.target_categories, id],
  }));

  const save = async () => {
    if (busy) return;
    if (!f.name.trim()) { toast.error(lang === "ar" ? "الاسم مطلوب" : "Name is required"); return; }
    setBusy(true);
    try {
      const body = {
        name: f.name.trim(),
        description: f.description,
        is_active: !!f.is_active,
        visible: !!f.visible,
        offer_type: f.offer_type,
        free_duration_days: Number(f.free_duration_days) || null,
        discount_percent: Number(f.discount_percent) || null,
        discount_amount: Number(f.discount_amount) || null,
        granted_plan: f.granted_plan || null,
        extra_benefits: f.extra_benefits.split(",").map(s => s.trim()).filter(Boolean),
        target_categories: f.target_categories,
        target_cities: f.target_cities.split(",").map(s => s.trim()).filter(Boolean),
        requires_verification: !!f.requires_verification,
        requires_kyc: !!f.requires_kyc,
        required_activity: f.required_activity.split(",").map(s => s.trim()).filter(Boolean),
        requires_prior_subscription: !!f.requires_prior_subscription,
        requires_admin_approval: !!f.requires_admin_approval,
        max_beneficiaries: Number(f.max_beneficiaries) || null,
        tags: f.tags.split(",").map(s => s.trim()).filter(Boolean),
      };
      if (f.start_at) body.start_at = new Date(f.start_at + "T00:00:00Z").toISOString();
      if (f.end_at)   body.end_at   = new Date(f.end_at   + "T23:59:59Z").toISOString();
      if (isEdit) {
        await apiClient.patch(`/admin/campaigns/${seed.id}`, body);
        toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      } else {
        await apiClient.post("/admin/campaigns", body);
        toast.success(lang === "ar" ? "تم إنشاء الحملة" : "Created");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || (lang === "ar" ? "تعذّر الحفظ" : "Failed to save"));
    } finally {
      setBusy(false);
    }
  };

  const H = ({ children }) => (
    <div className="text-[11px] font-bold uppercase tracking-widest text-[#8a6f2e] mt-3 mb-2">{children}</div>
  );
  const L = ({ children }) => (
    <label className="block text-[12px] font-semibold text-[#404040] mb-1">{children}</label>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="campaign-form-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#0a0a0a]/[0.08]">
          <h3 className="font-display text-xl flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#a8893e]" />
            {isEdit
              ? (lang === "ar" ? "تعديل الحملة" : "Edit campaign")
              : (lang === "ar" ? "حملة جديدة" : "New campaign")}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#0a0a0a]/5" data-testid="campaign-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <H>{lang === "ar" ? "المعلومات الأساسية" : "Basics"}</H>
          <div>
            <L>{lang === "ar" ? "اسم الحملة" : "Campaign name"}</L>
            <input className="input-dark w-full" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="cf-name" />
          </div>
          <div>
            <L>{lang === "ar" ? "الوصف" : "Description"}</L>
            <textarea rows={2} className="input-dark w-full" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="cf-description" />
          </div>

          <H>{lang === "ar" ? "نوع العرض" : "Offer"}</H>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <L>{lang === "ar" ? "نوع العرض" : "Offer type"}</L>
              <select className="input-dark w-full" value={f.offer_type} onChange={(e) => set("offer_type", e.target.value)} data-testid="cf-offer-type">
                {OFFER_TYPES.map((o) => <option key={o.id} value={o.id}>{lang === "ar" ? o.ar : o.en}</option>)}
              </select>
            </div>
            <div>
              <L>{lang === "ar" ? "الباقة الممنوحة" : "Granted plan"}</L>
              <select className="input-dark w-full" value={f.granted_plan} onChange={(e) => set("granted_plan", e.target.value)} data-testid="cf-plan">
                {PLANS.map((p) => <option key={p.id || "none"} value={p.id}>{lang === "ar" ? p.ar : p.en}</option>)}
              </select>
            </div>
            <div>
              <L>{lang === "ar" ? "مدة الاشتراك المجاني (أيام)" : "Free days"}</L>
              <input type="number" min={0} className="input-dark w-full" value={f.free_duration_days} onChange={(e) => set("free_duration_days", e.target.value)} data-testid="cf-free-days" />
            </div>
            <div>
              <L>{lang === "ar" ? "نسبة الخصم %" : "Discount %"}</L>
              <input type="number" min={0} max={100} className="input-dark w-full" value={f.discount_percent} onChange={(e) => set("discount_percent", e.target.value)} data-testid="cf-discount-pct" />
            </div>
            <div>
              <L>{lang === "ar" ? "قيمة الخصم الثابتة" : "Fixed discount"}</L>
              <input type="number" min={0} className="input-dark w-full" value={f.discount_amount} onChange={(e) => set("discount_amount", e.target.value)} data-testid="cf-discount-amt" />
            </div>
            <div>
              <L>{lang === "ar" ? "الحد الأقصى للمستفيدين" : "Max beneficiaries"}</L>
              <input type="number" min={0} className="input-dark w-full" value={f.max_beneficiaries} onChange={(e) => set("max_beneficiaries", e.target.value)} data-testid="cf-max-benef" />
            </div>
          </div>
          <div>
            <L>{lang === "ar" ? "مزايا إضافية (مفصولة بفاصلة)" : "Extra benefits (comma separated)"}</L>
            <input className="input-dark w-full" value={f.extra_benefits} onChange={(e) => set("extra_benefits", e.target.value)} data-testid="cf-benefits" />
          </div>

          <H>{lang === "ar" ? "المدة" : "Timing"}</H>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <L>{lang === "ar" ? "تاريخ البداية" : "Start date"}</L>
              <input type="date" className="input-dark w-full" value={f.start_at} onChange={(e) => set("start_at", e.target.value)} data-testid="cf-start" />
            </div>
            <div>
              <L>{lang === "ar" ? "تاريخ النهاية" : "End date"}</L>
              <input type="date" className="input-dark w-full" value={f.end_at} onChange={(e) => set("end_at", e.target.value)} data-testid="cf-end" />
            </div>
          </div>

          <H>{lang === "ar" ? "الاستهداف" : "Targeting"}</H>
          <div>
            <L>{lang === "ar" ? "الفئات المستهدفة (فارغ = الكل)" : "Target categories (empty = everyone)"}</L>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_OPTIONS.map((c) => {
                const on = f.target_categories.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleCategory(c.id)}
                    className={`text-[12px] px-3 py-1.5 rounded-full border ${on ? "bg-[#fbf7ec] border-[#c9a55c] text-[#5a4615]" : "border-[#0a0a0a]/[0.1] text-[#525252]"}`}
                    data-testid={`cf-cat-${c.id}`}
                  >
                    {lang === "ar" ? c.ar : c.en}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <L>{lang === "ar" ? "المدن (مفصولة بفاصلة، فارغ = جميع المدن)" : "Cities (comma separated, empty = all)"}</L>
            <input className="input-dark w-full" placeholder={lang === "ar" ? "مثال: ينبع, جدة" : "e.g. Yanbu, Jeddah"} value={f.target_cities} onChange={(e) => set("target_cities", e.target.value)} data-testid="cf-cities" />
          </div>
          <div>
            <L>{lang === "ar" ? "نشاط الأعمال المطلوب (مفصول بفاصلة)" : "Required business activity (comma separated)"}</L>
            <input className="input-dark w-full" value={f.required_activity} onChange={(e) => set("required_activity", e.target.value)} data-testid="cf-activity" />
          </div>

          <H>{lang === "ar" ? "شروط الاستحقاق" : "Eligibility"}</H>
          <div className="grid sm:grid-cols-2 gap-2 text-[13px]">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.requires_verification} onChange={(e) => set("requires_verification", e.target.checked)} data-testid="cf-req-verify" />{lang === "ar" ? "يتطلب حساب موثّق" : "Requires verified"}</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.requires_kyc} onChange={(e) => set("requires_kyc", e.target.checked)} data-testid="cf-req-kyc" />{lang === "ar" ? "يتطلب KYC" : "Requires KYC"}</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.requires_prior_subscription} onChange={(e) => set("requires_prior_subscription", e.target.checked)} data-testid="cf-req-prior" />{lang === "ar" ? "يتطلب اشتراكاً سابقاً" : "Requires prior subscription"}</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.requires_admin_approval} onChange={(e) => set("requires_admin_approval", e.target.checked)} data-testid="cf-req-admin" />{lang === "ar" ? "يتطلب موافقة الإدارة" : "Requires admin approval"}</label>
          </div>

          <H>{lang === "ar" ? "الحالة والوسوم" : "Status & tags"}</H>
          <div className="grid sm:grid-cols-2 gap-2 text-[13px]">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_active} onChange={(e) => set("is_active", e.target.checked)} data-testid="cf-active" />{lang === "ar" ? "مفعّلة" : "Active"}</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.visible} onChange={(e) => set("visible", e.target.checked)} data-testid="cf-visible" />{lang === "ar" ? "ظاهرة للمستخدم" : "Visible to users"}</label>
          </div>
          <div>
            <L>{lang === "ar" ? "وسوم (مفصولة بفاصلة)" : "Tags (comma separated)"}</L>
            <input className="input-dark w-full" placeholder="ramadan, launch, national-day" value={f.tags} onChange={(e) => set("tags", e.target.value)} data-testid="cf-tags" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-[#0a0a0a]/[0.08] sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="cf-cancel">
            {lang === "ar" ? "إلغاء" : "Cancel"}
          </button>
          <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="cf-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {isEdit ? (lang === "ar" ? "حفظ التعديلات" : "Save") : (lang === "ar" ? "إنشاء" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
}
